import { useState } from 'react'
import {
  Database,
  MapPin,
  Scale,
  Package,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Exchange {
  name: string
  amount: number
  unit: string
  type?: string
  location?: string
  categories?: string[]
  input?: boolean
}

// New nested structure from read_activity
interface ReadActivityDataNested {
  activity_code?: string
  database_name?: string
  basic_info?: {
    name: string
    unit: string
    location: string
    reference_product?: string
    activity_type?: string
  }
  description?: {
    process_description?: string
    product_description?: string
    classification?: string
    synonyms?: string[]
  }
  exchanges_summary?: {
    technosphere_inputs: number
    reference_outputs: number
    biosphere_flows: number
  }
  external_links?: {
    ecoquery_url?: string | null
  }
}

// Old flat structure
interface ReadActivityDataFlat {
  name: string
  code: string
  location: string
  unit: string
  reference_product?: string
  database?: string
  exchanges?: Exchange[]
  production_volume?: number
  comment?: string
  ecoquery_url?: string | null
  [key: string]: unknown
}

type ReadActivityData = ReadActivityDataNested | ReadActivityDataFlat

interface ReadActivityRendererProps {
  data: ReadActivityData
}

// Helper to normalize data to a common format
function normalizeActivityData(data: ReadActivityData): {
  name: string
  code: string
  location: string
  unit: string
  reference_product?: string
  database?: string
  ecoquery_url?: string | null
  exchanges?: Exchange[]
  comment?: string
  exchanges_summary?: {
    technosphere_inputs: number
    reference_outputs: number
    biosphere_flows: number
  }
} {
  // Check if it's the new nested structure
  if ('basic_info' in data && data.basic_info) {
    const nested = data as ReadActivityDataNested
    return {
      name: nested.basic_info!.name,
      code: nested.activity_code || '',
      location: nested.basic_info!.location,
      unit: nested.basic_info!.unit,
      reference_product: nested.basic_info!.reference_product,
      database: nested.database_name,
      ecoquery_url: nested.external_links?.ecoquery_url,
      comment: nested.description?.process_description,
      exchanges_summary: nested.exchanges_summary,
    }
  }

  // It's the flat structure
  const flat = data as ReadActivityDataFlat
  return {
    name: flat.name,
    code: flat.code,
    location: flat.location,
    unit: flat.unit,
    reference_product: flat.reference_product,
    database: flat.database,
    ecoquery_url: flat.ecoquery_url,
    exchanges: flat.exchanges,
    comment: flat.comment,
  }
}

function ExchangeRow({ exchange, isOutput }: { exchange: Exchange; isOutput: boolean }) {
  // Format amount nicely
  const formattedAmount =
    Math.abs(exchange.amount) < 0.001 && exchange.amount !== 0
      ? exchange.amount.toExponential(2)
      : exchange.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1 rounded text-xs',
        isOutput ? 'bg-success-muted/30' : 'bg-muted/30',
      )}
    >
      {isOutput ? (
        <ArrowRight className="w-3 h-3 text-success flex-shrink-0" />
      ) : (
        <ArrowLeft className="w-3 h-3 text-muted-foreground flex-shrink-0" />
      )}
      <span className="flex-1 truncate" title={exchange.name}>
        {exchange.name}
      </span>
      <span className="font-mono text-muted-foreground whitespace-nowrap">
        {formattedAmount} {exchange.unit}
      </span>
      {exchange.location && (
        <span className="text-[10px] text-muted-foreground">[{exchange.location}]</span>
      )}
    </div>
  )
}

export function ReadActivityRenderer({ data }: ReadActivityRendererProps) {
  const [showAllExchanges, setShowAllExchanges] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)

  // Normalize the data structure
  const normalized = normalizeActivityData(data)

  const exchanges = normalized.exchanges || []

  // Description truncation
  const descriptionLimit = 150
  const hasLongDescription = normalized.comment && normalized.comment.length > descriptionLimit
  const displayDescription =
    hasLongDescription && !showFullDescription
      ? normalized.comment!.slice(0, descriptionLimit) + '...'
      : normalized.comment

  // Separate inputs and outputs
  const outputs = exchanges.filter(
    (e) =>
      e.type === 'production' ||
      e.type === 'reference product' ||
      (e.input === false && e.amount > 0),
  )
  const inputs = exchanges.filter(
    (e) =>
      e.type === 'technosphere' ||
      e.type === 'from technosphere' ||
      (e.input !== false && !outputs.includes(e)),
  )
  const emissions = exchanges.filter(
    (e) =>
      e.type === 'biosphere' ||
      e.type === 'to environment' ||
      e.categories?.includes('air') ||
      e.categories?.includes('water') ||
      e.categories?.includes('soil'),
  )

  // Limit visible exchanges
  const maxVisible = 5
  const visibleInputs = showAllExchanges ? inputs : inputs.slice(0, maxVisible)
  const visibleEmissions = showAllExchanges ? emissions : emissions.slice(0, maxVisible)
  const totalExchanges = inputs.length + emissions.length
  const hasMore = totalExchanges > maxVisible * 2

  return (
    <div className="space-y-3">
      {/* Activity header */}
      <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Database className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {normalized.ecoquery_url ? (
              <a
                href={normalized.ecoquery_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold leading-tight text-primary hover:underline inline-flex items-center gap-1"
              >
                {normalized.name}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : (
              <h3 className="text-sm font-semibold leading-tight">{normalized.name}</h3>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {normalized.location}
              </span>
              <span className="inline-flex items-center gap-1">
                <Scale className="w-3 h-3" />
                {normalized.unit}
              </span>
              {normalized.reference_product && (
                <span className="inline-flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  {normalized.reference_product}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Activity code */}
        <code className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
          {normalized.code}
        </code>
      </div>

      {/* Compact inline summary - database + exchange counts */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {normalized.database && (
          <span className="font-medium">
            {normalized.database.replace('ecoinvent-', 'ei').replace('-cutoff', '')}
          </span>
        )}
        {(inputs.length > 0 || outputs.length > 0 || emissions.length > 0) && (
          <span>
            {inputs.length} inputs · {outputs.length} outputs
            {emissions.length > 0 && ` · ${emissions.length} emissions`}
          </span>
        )}
        {normalized.exchanges_summary && !exchanges.length && (
          <span>
            {normalized.exchanges_summary.technosphere_inputs} inputs ·{' '}
            {normalized.exchanges_summary.reference_outputs} outputs ·{' '}
            {normalized.exchanges_summary.biosphere_flows} biosphere
          </span>
        )}
      </div>

      {/* Description with expand/collapse */}
      {normalized.comment && (
        <div className="text-xs text-muted-foreground">
          <span className="italic">{displayDescription}</span>
          {hasLongDescription && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="ml-1 text-primary hover:underline font-medium"
            >
              {showFullDescription ? 'show less' : 'more...'}
            </button>
          )}
        </div>
      )}

      {/* Reference output */}
      {outputs.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-success uppercase tracking-wide">
            Reference Output
          </h4>
          {outputs.slice(0, 1).map((exchange, i) => (
            <ExchangeRow key={i} exchange={exchange} isOutput />
          ))}
        </div>
      )}

      {/* Inputs */}
      {visibleInputs.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Technosphere Inputs ({inputs.length})
          </h4>
          <div className="space-y-0.5">
            {visibleInputs.map((exchange, i) => (
              <ExchangeRow key={i} exchange={exchange} isOutput={false} />
            ))}
          </div>
        </div>
      )}

      {/* Emissions (if not too many) */}
      {visibleEmissions.length > 0 && emissions.length <= 10 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-warning uppercase tracking-wide">
            Emissions ({emissions.length})
          </h4>
          <div className="space-y-0.5">
            {visibleEmissions.map((exchange, i) => (
              <ExchangeRow key={i} exchange={exchange} isOutput />
            ))}
          </div>
        </div>
      )}

      {/* Show more/less */}
      {hasMore && (
        <button
          onClick={() => setShowAllExchanges(!showAllExchanges)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {showAllExchanges ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Show all {totalExchanges} exchanges
            </>
          )}
        </button>
      )}
    </div>
  )
}
